import { useQueryClient } from "@tanstack/react-query";
import React, { useCallback } from "react";
import { Navigate, useParams } from "react-router-dom";

import { Headline, Paragraph } from "@fronterahealth/frontera-ui-components";

import { useLearnerEhrProgrammingQuery } from "@api/graphql/types-and-hooks";
import { EhrRefreshControl } from "@components/EhrRefreshControl/EhrRefreshControl";
import { useLearnerProgrammingRefresh } from "@hooks/useLearnerProgrammingRefresh";
import { EhrProgrammingItemDetail } from "@pages/ClientEhrProgramming/EhrProgrammingItemDetail";
import { EhrProgrammingSidebar } from "@pages/ClientEhrProgramming/EhrProgrammingSidebar";
import { EhrSessionDataSection } from "@pages/ClientEhrProgramming/EhrSessionDataSection";
import { ehrGoalPath } from "@pages/ClientEhrProgramming/ehrProgrammingPaths";
import { RefreshingIndicator } from "@pages/ClinicalReportDetails/ReportPage/Components/RefreshingIndicator";

// A learner-level refresh, with no report. reassessmentReportGoalSync doubles as
// FDM's "force" flag: without it, a client that has imported before is refused
// ("Import already completed"). With no report id it fills no report.
const LEARNER_REFRESH_VARIABLES = { reassessmentReportGoalSync: true };

interface ClientEhrProgrammingProps {
  // The programming/unmatched route: show session data not tied to any listed goal or target.
  showUnmatched?: boolean;
}

const PaneMessage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex justify-center p-8">
    <Paragraph colorType="secondary">{children}</Paragraph>
  </div>
);

// Internal-only view of everything stored on the learner's EHR goal and target rows:
// goals and targets on the left, the selected one on the right (routed by id).
export const ClientEhrProgramming: React.FC<ClientEhrProgrammingProps> = ({ showUnmatched = false }) => {
  const { clientId, goalId, targetId } = useParams();
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useLearnerEhrProgrammingQuery(
    { learnerId: clientId! },
    { enabled: !!clientId, queryKey: ["learner-ehr-programming", clientId] },
  );

  const onSyncCompleted = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["learner-ehr-programming", clientId] });
    void queryClient.invalidateQueries({ queryKey: ["learner-ehr-session-data", clientId] });
    void queryClient.invalidateQueries({ queryKey: ["learner-ehr-syncs", clientId] });
  }, [queryClient, clientId]);

  const refreshState = useLearnerProgrammingRefresh({
    learnerId: clientId ?? "",
    startVariables: LEARNER_REFRESH_VARIABLES,
    onSyncCompleted,
  });

  const programming = data?.learnerEhrProgramming;
  const goals = programming?.goals ?? [];
  const targetCount = goals.reduce((count, goal) => count + goal.targets.length, 0);

  const header = (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-4">
        <Headline size="small">Stored EHR Programming</Headline>
        <EhrRefreshControl refreshState={refreshState} analyticsSource="ehr_data_programming" />
      </div>
      {programming && (
        <Paragraph size="small" colorType="secondary">
          {programming.organizationType} · {goals.length} goals · {targetCount} targets. Everything we store from this
          client&apos;s EHR: every goal and target field (including empty ones), phase and event history, and session
          data. Rows the sync marked not in use are hidden.
        </Paragraph>
      )}
    </div>
  );

  let body: React.ReactNode;
  if (refreshState.isSyncing) {
    body = (
      <div className="flex min-h-[calc(100vh-20rem)] items-center justify-center">
        <RefreshingIndicator />
      </div>
    );
  } else if (isLoading) {
    body = <PaneMessage>Loading...</PaneMessage>;
  } else if (isError || !programming) {
    body = <PaneMessage>Couldn&apos;t load stored EHR programming.</PaneMessage>;
  } else {
    const selectedGoal = goals.find((goal) => goal.id === goalId);
    const selectedTarget = targetId ? selectedGoal?.targets.find((target) => target.id === targetId) : undefined;

    let pane: React.ReactNode;
    if (showUnmatched) {
      pane = (
        <div className="flex flex-col gap-3 p-5 w-full bg-white rounded-lg border border-limestone-200">
          <Headline size="extra-small">Unmatched session data</Headline>
          <Paragraph size="small" colorType="secondary">
            Data points for this client that aren&apos;t tied to any goal or target on this page.
          </Paragraph>
          <EhrSessionDataSection
            learnerId={clientId!}
            scope={{ unmatched: true }}
            summary={programming.unmatchedSessionSummary}
            label="Unmatched data points"
          />
        </div>
      );
    } else if (!goalId) {
      pane = goals.length ? (
        <Navigate to={ehrGoalPath(clientId!, goals[0].id)} replace />
      ) : (
        <PaneMessage>No EHR programming stored for this client.</PaneMessage>
      );
    } else if (!selectedGoal || (targetId && !selectedTarget)) {
      pane = <PaneMessage>This goal or target isn&apos;t stored for this client (or is no longer in use).</PaneMessage>;
    } else {
      pane = (
        <EhrProgrammingItemDetail
          // Remount per item so open sections and session-data pages don't carry over.
          key={selectedTarget?.id ?? selectedGoal.id}
          learnerId={clientId!}
          goal={selectedGoal}
          target={selectedTarget}
        />
      );
    }

    body = (
      <div className="flex items-start gap-4">
        <EhrProgrammingSidebar
          clientId={clientId!}
          goals={goals}
          selectedGoalId={goalId}
          selectedTargetId={targetId}
          unmatchedDataPointCount={programming.unmatchedSessionSummary.dataPointCount}
          isUnmatchedSelected={showUnmatched}
        />
        <div className="min-w-0 flex-1">{pane}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 w-full">
      {header}
      {body}
    </div>
  );
};
